// types/roles.ts
export type UserType =
  | "ejecutivo_ventas"
  | "gerente_ventas"
  | "administrador_crm"
  | "dueno_negocio"
  | "soporte_cliente";

export interface Role {
  id: string;
  name: string;
  description: string;
  is_system_role: boolean;
  permissions: string[] | { all?: boolean };
  company_id: string;
  slug: string;
  user_type?: UserType;
  created_at: string;
  updated_at: string;
  users_count?: number;
  is_active?: boolean;
  sales_quota?: number; // ✅ NUEVO: Meta de ventas asignada al rol (ej: 20000)
  userTypeName?: string; // Para UI
  canEdit?: boolean; // Para UI
  canDelete?: boolean; // Para UI
}

export interface CreateRoleRequest {
  company_id: string;
  slug: string;
  name: string;
  description?: string;
  is_system_role?: boolean;
  permissions: string[] | { all: true };
  user_type?: UserType;
  sales_quota?: number; // ✅ NUEVO
}

export interface UIRole {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  usersCount?: number;
  isActive: boolean;
  isSystem: boolean;
  createdAt: Date;
  updatedAt: Date;
  companyId: string;
  slug: string;
  userType?: UserType;
  userTypeName?: string;
  salesQuota?: number; // ✅ NUEVO
}

export interface RoleStatistics {
  totalRoles: number;
  activeRoles: number;
  totalUsers: number;
  systemRoles: number;
  customRoles?: number;
  byUserType?: Record<UserType, number>;
}

export interface Permission {
  id: string;
  key: string;
  name: string;
  category: string;
  description?: string;
  requiresPlan?: string[];
}

export const USER_TYPE_NAMES: Record<UserType, string> = {
  ejecutivo_ventas: "Ejecutivo de Ventas",
  gerente_ventas: "Gerente de Ventas",
  administrador_crm: "Administrador Prographic",
  dueno_negocio: "Dueño de Negocio",
  soporte_cliente: "Soporte / Servicio al Cliente",
} as const;

export const USER_TYPE_DESCRIPTIONS: Record<UserType, string> = {
  ejecutivo_ventas:
    "Gestiona contactos, crea oportunidades y da seguimiento al pipeline de ventas",
  gerente_ventas:
    "Supervisa el equipo, analiza oportunidades y crea reportes de ventas",
  administrador_crm:
    "Administra usuarios, roles, seguridad y configuración del sistema",
  dueno_negocio:
    "Visualiza el desempeño del equipo y toma decisiones estratégicas",
  soporte_cliente:
    "Mantiene la infraestructura y responde a solicitudes de soporte",
} as const;

export function isRole(obj: unknown): obj is Role {
  const role = obj as Role | null;
  return (
    !!role &&
    typeof role.id === "string" &&
    typeof role.name === "string" &&
    typeof role.company_id === "string" &&
    typeof role.slug === "string"
  );
}

export function transformRoleForUI(role: Role): UIRole {
  return {
    id: role.id,
    name: role.name,
    description: role.description || "",
    permissions: Array.isArray(role.permissions) ? role.permissions : [],
    usersCount: role.users_count || 0,
    isActive: role.is_active !== false,
    isSystem: role.is_system_role,
    createdAt: new Date(role.created_at),
    updatedAt: new Date(role.updated_at),
    companyId: role.company_id,
    slug: role.slug,
    userType: role.user_type,
    userTypeName: role.user_type ? USER_TYPE_NAMES[role.user_type] : undefined,
    salesQuota: role.sales_quota, // ✅ NUEVO
  };
}

export function transformRoleForAPI(
  uiRole: Partial<UIRole>,
): Partial<CreateRoleRequest> {
  return {
    name: uiRole.name,
    description: uiRole.description,
    permissions: uiRole.permissions || [],
    user_type: uiRole.userType,
    is_system_role: uiRole.isSystem,
    sales_quota: uiRole.salesQuota, // ✅ NUEVO
  };
}

export const USER_TYPE_PERMISSIONS: Record<UserType, string[]> = {
  ejecutivo_ventas: [
    "contacts.view",
    "contacts.create",
    "contacts.update",
    "opportunities.view",
    "opportunities.create",
    "opportunities.update",
    "tasks.create",
    "tasks.view",
    "tasks.update",
    "calls.create",
    "calls.view",
    "calls.update",
    "meetings.create",
    "meetings.view",
    "meetings.update",
    "pipeline.view",
    "pipeline.update",
    "client_companies.view",
    "client_companies.create",
    "client_companies.update",
    "products.view",

  ],
  gerente_ventas: [
    "contacts.view",
    "contacts.create",
    "contacts.update",
    "contacts.delete",
    "opportunities.view",
    "opportunities.create",
    "opportunities.update",
    "opportunities.delete",
    "client_companies.view",
    "client_companies.create",
    "client_companies.update",
    "client_companies.delete",
    "products.view",
    "products.create",
    "products.update",

    "reports.view",
    "pipeline.view",
    "tasks.assign",
  ],
  administrador_crm: [
    "all.view",
    "users.view",
    "users.create",
    "users.update",
    "users.delete",
    "roles.view",
    "roles.create",
    "roles.update",
    "roles.delete",
    "settings.view",
    "settings.update",
    "client_companies.view",
    "client_companies.create",
    "client_companies.update",
    "client_companies.delete",
    "contacts.view",
    "contacts.create",
    "contacts.update",
    "contacts.delete",
    "opportunities.view",
    "opportunities.create",
    "opportunities.update",
    "opportunities.delete",
    "products.view",
    "products.create",
    "products.update",
    "products.delete",

    "reports.view",
  ],
  dueno_negocio: ["all.view", "reports.view", "dashboard.view"],
  soporte_cliente: [
    "system.access",
    "tickets.view",
    "tickets.respond",
  ],
};

export const PERMISSIONS_BY_CATEGORY: Record<
  string,
  Array<{ id: string; name: string }>
> = {
  Dashboard: [{ id: "dashboard.view", name: "Ver Dashboard" }],
  Empresas: [
    { id: "client_companies.view", name: "Ver Empresas" },
    { id: "client_companies.create", name: "Crear Empresas" },
    { id: "client_companies.update", name: "Editar Empresas" },
    { id: "client_companies.delete", name: "Eliminar Empresas" },
  ],
  Contactos: [
    { id: "contacts.view", name: "Ver Contactos" },
    { id: "contacts.create", name: "Crear Contactos" },
    { id: "contacts.update", name: "Editar Contactos" },
    { id: "contacts.delete", name: "Eliminar Contactos" },
  ],
  Oportunidades: [
    { id: "opportunities.view", name: "Ver Oportunidades" },
    { id: "opportunities.create", name: "Crear Oportunidades" },
    { id: "opportunities.update", name: "Editar Oportunidades" },
    { id: "opportunities.delete", name: "Eliminar Oportunidades" },
  ],
  Productos: [
    { id: "products.view", name: "Ver Productos" },
    { id: "products.create", name: "Crear Productos" },
    { id: "products.update", name: "Editar Productos" },
    { id: "products.delete", name: "Eliminar Productos" },
  ],
  Personal: [
    { id: "users.view", name: "Ver lista de Personal" },
    { id: "users.create", name: "Dar de alta nuevos miembros" },
    { id: "users.update", name: "Modificar información y habilidades" },
    { id: "users.delete", name: "Desactivar miembros del equipo" },
  ],
  Roles: [
    { id: "roles.view", name: "Ver niveles de acceso" },
    { id: "roles.create", name: "Crear nuevos niveles de acceso" },
    { id: "roles.update", name: "Modificar permisos de roles" },
    { id: "roles.delete", name: "Eliminar niveles de acceso" },
  ],
  Asistencia: [
    { id: "attendance.read", name: "Ver asistencia y dashboard" },
    { id: "attendance.setup", name: "Configurar horarios y evaluaciones" },
    { id: "attendance.upload", name: "Cargar asistencia por archivo" },
  ],
  Inventario: [
    { id: "inventory.read", name: "Ver inventario y configuraciones" },
    { id: "inventory.manage", name: "Crear y editar materia prima" },
    { id: "inventory.configure", name: "Configurar consumos por estación" },
  ],
  Configuración: [
    { id: "settings.view", name: "Ver configuración general" },
    { id: "settings.update", name: "Modificar ajustes del sistema" },
  ],
} as const;
